// Quotes view for the portal — list of cards, each expandable to show
// line items. Queries portal_quotes (the view), NEVER raw quotes.
//
// Status display: reuses src/pages/payments/quote-status.ts — its
// customerLabel field already maps operator-only states like 'sent' /
// 'viewed' to 'Awaiting approval' so portal users don't see internal
// workflow vocabulary. The quote-status helper was authored anticipating
// this consumer (see its file header).
//
// Line items live as JSONB in the view; we surface them via a typed
// shape. The keys observed across all rows on dev are
// {id, name, description, qty, unit, unit_price, total} — no cost /
// margin fields exist in the blob (recon-confirmed before exposure).

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate } from '@/lib/utils'
import { ChevronDown, ChevronUp, FileText, AlertCircle, Check, X, Loader2, CheckCircle2, XCircle, Download, ThumbsUp } from 'lucide-react'
import { getQuoteStatusConfig, getCustomerLabel } from '@/pages/payments/quote-status'
import { toast } from 'sonner'
import { downloadPortalPdf } from '@/lib/portalPdf'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

interface QuoteLineItem {
  id: string | number
  name: string
  description?: string | null
  qty: number
  unit?: string | null
  unit_price: number
  total: number
}

interface PortalQuote {
  id: string
  quote_number: string
  title: string | null
  status: string
  line_items: QuoteLineItem[] | null
  subtotal: number | null
  discount_amount: number | null
  tax_rate: number | null
  tax_amount: number | null
  total: number | null
  currency: string | null
  issue_date: string | null
  valid_until: string | null
  notes: string | null
  sent_at: string | null
  approved_at: string | null
  declined_at: string | null
  created_at: string | null
  portal_responded_by: string | null
  portal_responded_by_name: string | null
  portal_response_note: string | null
  recommended_at: string | null
  recommended_by: string | null
  recommended_by_name: string | null
}

export default function PortalQuotes() {
  const { portalUser } = useAuth()
  const role = portalUser?.role ?? 'member'

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ['portal-quotes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_quotes')
        .select('id, quote_number, title, status, line_items, subtotal, discount_amount, tax_rate, tax_amount, total, currency, issue_date, valid_until, notes, sent_at, approved_at, declined_at, created_at, portal_responded_by, portal_responded_by_name, portal_response_note, recommended_at, recommended_by, recommended_by_name')
        .order('created_at', { ascending: false, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as PortalQuote[]
    },
  })

  // Threshold readable via the existing companies portal_read policy
  // (id = get_portal_company_id()) — confirms in recon. NULL = no cap
  // → today's behavior for members; admins are never capped at all.
  const { data: threshold = null } = useQuery({
    queryKey: ['portal-threshold', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return null
      const { data } = await supabase.from('companies')
        .select('portal_approval_threshold').eq('id', portalUser.company_id).maybeSingle()
      return data?.portal_approval_threshold ?? null
    },
    enabled: !!portalUser?.company_id,
  })

  if (isLoading) {
    return <PortalSkeletonList count={3} height={140} />
  }
  if (quotes.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <FileText style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No quotes yet</p>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>When your service provider sends a quote, it'll land here with line items and a one-click approve.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {quotes.map(q => <PortalQuoteCard key={q.id} quote={q} role={role} threshold={threshold} />)}
    </div>
  )
}

function PortalQuoteCard({ quote, role, threshold }: { quote: PortalQuote; role: 'admin' | 'member' | 'viewer'; threshold: number | null }) {
  const [expanded, setExpanded] = useState(false)
  const [responding, setResponding] = useState<null | 'approve' | 'decline' | 'recommend'>(null)
  const cfg = getQuoteStatusConfig(quote.status)
  const label = getCustomerLabel(quote.status)
  const currency = quote.currency ?? 'USD'
  const expired = useMemo(() => {
    if (!quote.valid_until) return false
    return new Date(quote.valid_until) < new Date() && quote.status !== 'approved' && quote.status !== 'declined'
  }, [quote.valid_until, quote.status])

  const canRespond = (quote.status === 'sent' || quote.status === 'viewed') && !expired
  const respondedAt = quote.status === 'approved' ? quote.approved_at
    : quote.status === 'declined' ? quote.declined_at
    : null

  // Action gating matches database truth (the portal_quote_response_guard
  // trigger): member-over-threshold can ONLY recommend — approve AND
  // decline are both blocked by the trigger's response-column gate.
  // Admins are never threshold-capped.
  const memberOverThreshold = role === 'member' && threshold !== null && (quote.total ?? 0) > threshold
  const alreadyRecommended = !!quote.recommended_at && quote.status !== 'approved' && quote.status !== 'declined'

  return (
    <article style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{quote.quote_number}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {label}
          </span>
          {expired && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid var(--warning-border)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <AlertCircle style={{ width: 10, height: 10 }} /> Expired
            </span>
          )}
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{quote.title ?? 'Untitled quote'}</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <KeyValue label="Total" value={formatCurrency(quote.total ?? 0, currency)} bold />
          {quote.valid_until && <KeyValue label="Valid until" value={formatDate(quote.valid_until)} />}
        </div>

        {/* Response stamp — present when the quote has been responded to.
            Reads cleanly even if portal_responded_by_name is null (legacy
            staff approvals predate the portal flow). */}
        {(quote.status === 'approved' || quote.status === 'declined') && respondedAt && (
          <ResponseStamp
            decision={quote.status as 'approved' | 'declined'}
            responderName={quote.portal_responded_by_name}
            respondedAt={respondedAt}
            note={quote.portal_response_note} />
        )}

        {/* Recommend-pending chip — visible to ALL roles when a member
            has recommended but no admin response yet. For admins this
            is the "your approval needed" cue. */}
        {alreadyRecommended && quote.recommended_at && (
          <RecommendStamp
            recommenderName={quote.recommended_by_name}
            recommendedAt={quote.recommended_at}
            note={quote.portal_response_note}
            emphasizeAdmin={role === 'admin'} />
        )}

        {/* Action row, role + threshold aware:
              - admin or member-under-threshold: Approve + Decline (today)
              - member-over-threshold: Recommend approval only (trigger
                blocks both approve AND decline)
              - viewer: no actions (also gated by route guard) */}
        {canRespond && !responding && role !== 'viewer' && (
          memberOverThreshold ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              <button type="button" onClick={() => setResponding('recommend')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                  padding: '8px 14px', background: 'var(--accent)',
                  border: 'none', borderRadius: 8, color: 'var(--accent-ink)',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                <ThumbsUp style={{ width: 12, height: 12 }} />
                {alreadyRecommended ? 'Re-recommend approval' : 'Recommend approval'}
              </button>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.5 }}>
                <AlertCircle style={{ width: 10, height: 10, display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                This quote exceeds your approval limit of {formatCurrency(threshold ?? 0, currency)}. An admin completes the approval.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button type="button" onClick={() => setResponding('approve')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', background: 'var(--positive)',
                  border: 'none', borderRadius: 8, color: 'var(--accent-ink)',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                <Check style={{ width: 12, height: 12 }} />
                Approve
              </button>
              <button type="button" onClick={() => setResponding('decline')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', background: 'var(--surface)',
                  border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                <X style={{ width: 12, height: 12 }} />
                Decline
              </button>
            </div>
          )
        )}

        {responding && (
          <ResponseDialog
            quote={quote}
            decision={responding}
            onCancel={() => setResponding(null)}
            onDone={() => setResponding(null)} />
        )}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(quote.line_items?.length ?? 0) > 0 && (
            <button type="button" onClick={() => setExpanded(e => !e)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', background: 'transparent',
                border: '1px solid var(--border-subtle)', borderRadius: 7,
                fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)',
                cursor: 'pointer',
              }}>
              {expanded ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
              {expanded ? 'Hide line items' : `View ${quote.line_items!.length} line item${quote.line_items!.length === 1 ? '' : 's'}`}
            </button>
          )}
          <DownloadQuotePdfButton id={quote.id} />
        </div>
      </div>

      {expanded && quote.line_items && (
        <div style={{ background: 'var(--canvas)', borderTop: '1px solid var(--border-subtle)', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {quote.line_items.map(li => (
              <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{li.name}</p>
                  {li.description && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0', lineHeight: 1.4 }}>{li.description}</p>}
                  <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                    {li.qty} {li.unit ?? ''} × {formatCurrency(li.unit_price, currency)}
                  </p>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', whiteSpace: 'nowrap' }}>{formatCurrency(li.total, currency)}</span>
              </div>
            ))}
          </div>
          {/* Totals breakdown */}
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {quote.subtotal != null && <TotalRow label="Subtotal" value={formatCurrency(quote.subtotal, currency)} />}
            {(quote.discount_amount ?? 0) > 0 && <TotalRow label="Discount" value={`−${formatCurrency(quote.discount_amount!, currency)}`} />}
            {(quote.tax_amount ?? 0) > 0 && <TotalRow label={`Tax${quote.tax_rate ? ` (${(quote.tax_rate * 100).toFixed(2)}%)` : ''}`} value={formatCurrency(quote.tax_amount!, currency)} />}
            <TotalRow label="Total" value={formatCurrency(quote.total ?? 0, currency)} bold />
          </div>
          {quote.notes && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Notes</p>
              <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{quote.notes}</p>
            </div>
          )}
        </div>
      )}
    </article>
  )
}

function KeyValue({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: 'var(--ink-primary)' }}>{value}</span>
    </div>
  )
}

function TotalRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: bold ? 13 : 12 }}>
      <span style={{ color: bold ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontWeight: bold ? 700 : 500 }}>{label}</span>
      <span style={{ color: 'var(--ink-primary)', fontWeight: bold ? 800 : 600 }}>{value}</span>
    </div>
  )
}

function DownloadQuotePdfButton({ id }: { id: string }) {
  const [busy, setBusy] = useState(false)
  const onClick = async () => {
    setBusy(true)
    const r = await downloadPortalPdf('quote', id)
    setBusy(false)
    if (!r.ok) toast.error(r.error || 'Download failed')
  }
  return (
    <button type="button" onClick={onClick} disabled={busy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '5px 10px', background: 'transparent',
        border: '1px solid var(--border-subtle)', borderRadius: 7,
        fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)',
        cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
      }}>
      {busy ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <Download style={{ width: 11, height: 11 }} />}
      {busy ? 'Preparing…' : 'Download PDF'}
    </button>
  )
}

function ResponseStamp({ decision, responderName, respondedAt, note }: {
  decision: 'approved' | 'declined'
  responderName: string | null
  respondedAt: string
  note: string | null
}) {
  const isApprove = decision === 'approved'
  return (
    <div style={{
      marginBottom: 10, padding: '10px 12px',
      background: isApprove ? 'var(--positive-dim)' : 'var(--danger-dim)',
      border: `1px solid ${isApprove ? 'var(--positive-border)' : 'var(--danger-border)'}`,
      borderRadius: 8,
    }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: isApprove ? 'var(--positive)' : 'var(--danger)' }}>
        {isApprove ? <CheckCircle2 style={{ width: 12, height: 12 }} /> : <XCircle style={{ width: 12, height: 12 }} />}
        {isApprove ? 'Approved' : 'Declined'}
        {responderName && <> by {responderName}</>}
        <> on {formatDate(respondedAt, 'MMM d, yyyy')}</>
      </div>
      {note && (
        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '6px 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {note}
        </p>
      )}
    </div>
  )
}

function ResponseDialog({ quote, decision, onCancel, onDone }: {
  quote: PortalQuote
  decision: 'approve' | 'decline' | 'recommend'
  onCancel: () => void
  onDone: () => void
}) {
  const { portalUser } = useAuth()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  const isApprove = decision === 'approve'
  const isRecommend = decision === 'recommend'
  // Decline + Approve flips status; Recommend keeps status, sets only the
  // recommend stamps (matches trigger's allowed columns for members).
  // The trigger then validates which columns moved.

  const responseMutation = useMutation({
    mutationFn: async () => {
      if (!portalUser) throw new Error('Portal session missing')
      const now = new Date().toISOString()
      const update: Record<string, unknown> = {
        portal_response_note: note.trim() || null,
      }
      if (isRecommend) {
        update.recommended_at = now
        update.recommended_by = portalUser.id
        // Status intentionally unchanged.
      } else {
        update.status = isApprove ? 'approved' : 'declined'
        update.portal_responded_by = portalUser.id
        if (isApprove) update.approved_at = now
        else update.declined_at = now
      }
      const { error } = await supabase.from('quotes').update(update).eq('id', quote.id)
      if (error) throw error
      // Best-effort notify; the write is already committed. quote-response-
      // notify self-detects from the quote's DB state — when status is
      // sent/viewed + recommended_at set, it sends the recommendation
      // email to admins; when status is approved/declined, it sends the
      // existing staff team email.
      const { data: notifyResp, error: notifyErr } = await supabase.functions.invoke<{ ok: boolean; notified: boolean; warning?: string }>(
        'quote-response-notify', { body: { quote_id: quote.id } },
      )
      if (notifyErr) return { notified: false, warning: notifyErr.message }
      return notifyResp ?? { notified: false }
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['portal-quotes'] })
      const action = isRecommend ? 'recommended' : isApprove ? 'approved' : 'declined'
      const audience = isRecommend ? 'admins notified' : 'team notified'
      if (resp?.notified) {
        toast.success(`Quote ${action} · ${audience}`)
      } else {
        toast.success(`Quote ${action}`)
        if (resp?.warning) toast.warning(`Email delayed: ${resp.warning}`)
      }
      onDone()
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to record response'),
  })

  const accent = isRecommend ? 'var(--accent)' : isApprove ? 'var(--positive)' : 'var(--danger)'
  const accentDim = isRecommend ? 'var(--accent-dim)' : isApprove ? 'var(--positive-dim)' : 'var(--danger-dim)'
  const accentBorder = isRecommend ? 'var(--accent-border)' : isApprove ? 'var(--positive-border)' : 'var(--danger-border)'
  const promptText = isRecommend
    ? `Recommend approving quote ${quote.quote_number}?`
    : isApprove ? `Approve quote ${quote.quote_number}?` : `Decline quote ${quote.quote_number}?`
  const placeholder = isRecommend
    ? 'Optional note for admins (why you recommend this)'
    : isApprove ? 'Optional comment (visible to your service provider)' : 'Optional reason (visible to your service provider)'
  const submitLabel = isRecommend ? 'Send to admins' : isApprove ? 'Confirm approval' : 'Confirm decline'

  return (
    <div style={{
      marginBottom: 10, padding: 12,
      background: accentDim,
      border: `1px solid ${accentBorder}`,
      borderRadius: 8,
    }}>
      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 8px' }}>
        {promptText}
      </p>
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        rows={isRecommend ? 3 : isApprove ? 2 : 3}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '8px 10px',
          background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 7,
          fontSize: 12, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box',
          resize: 'none', lineHeight: 1.5, marginBottom: 8,
        }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onCancel} disabled={responseMutation.isPending}
          style={{ flex: 1, padding: '7px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        <button type="button" onClick={() => responseMutation.mutate()} disabled={responseMutation.isPending}
          style={{
            flex: 1, padding: '7px', border: 'none', borderRadius: 7,
            background: accent,
            color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          {responseMutation.isPending && <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />}
          {responseMutation.isPending ? 'Submitting…' : submitLabel}
        </button>
      </div>
    </div>
  )
}

function RecommendStamp({ recommenderName, recommendedAt, note, emphasizeAdmin }: {
  recommenderName: string | null
  recommendedAt: string
  note: string | null
  emphasizeAdmin: boolean
}) {
  return (
    <div style={{
      marginBottom: 10, padding: '10px 12px',
      background: 'var(--info-dim)',
      border: '1px solid var(--info-border)',
      borderRadius: 8,
    }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--info)' }}>
        <ThumbsUp style={{ width: 12, height: 12 }} />
        Recommended
        {recommenderName && <> by {recommenderName}</>}
        <> on {formatDate(recommendedAt, 'MMM d, yyyy')}</>
      </div>
      {emphasizeAdmin && (
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--info)', margin: '4px 0 0' }}>
          Your approval needed
        </p>
      )}
      {note && (
        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '6px 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {note}
        </p>
      )}
    </div>
  )
}

